/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Box,
  Card,
  Grid,
  Link,
  Skeleton,
  Typography,
  useTheme,
} from '@mui/material';
import {Image} from '@jobseeker/lotus/components/Image';
import {AnimatePresence, motion} from 'framer-motion';
import {useEffect, useState} from 'react';

import useLoadAds from '@jobseeker/common-core/hooks/useLoadAds';
import type {
  AdsElementType,
  AdsEventInfo,
  AdsOptions,
  AppState,
  ProfileTags,
} from '@jobseeker/common-core/types';
import {
  getQueryParamsAndSeekerId,
  getUbaPageName,
  handleTracking,
  parseShowcaseUrl,
  TrackEventUBA,
} from '@jobseeker/common-core/utils';
import {Tooltip} from '@jobseeker/lotus/elements';
import {useInView} from '@jobseeker/lotus/hooks/observer';
import {useRouter} from 'next/router';
import {shallowEqual, useSelector} from 'react-redux';
import type {AdElement} from '../../types';
import {useMediaQuery} from '@mui/material';
import {openUrl, deferTask} from '@jobseeker/common-core/utils/common';

type VerticalFrameProps = {
  adParams: AdElement;
  adsInfo: AdsElementType[];
  direction: string;
  adsEventInfo?: AdsEventInfo;
  source?: string;
  options?: AdsOptions;
  titleOut?: boolean;
};

const VerticalFrame = ({
  adParams,
  adsInfo,
  direction,
  options,
  source,
  adsEventInfo,
  titleOut = false,
}: VerticalFrameProps) => {
  const [ads, setAds] = useState(Array<AdsElementType>);
  const [showSkeleton, setShowSkeleton] = useState(true);
  const [adsLoaded, setAdsLoaded] = useState(false);
  const theme = useTheme();
  const isMdOrLg = useMediaQuery(theme.breakpoints.between('md', 'xl'));
  const isXsOrXss = useMediaQuery(theme.breakpoints.between('xxs', 'xs'));
  const {userId, isLoggedIn} = useSelector(
    (state: AppState) => state.auth,
    shallowEqual
  );

  const {slot, label, showTitle} = adParams;
  const {
    profile: {tags, id},
  } = useSelector((state: AppState) => state.user, shallowEqual);

  const page = useSelector((state: AppState) => state.app.page, shallowEqual);

  const [adOptions, setAdOptions] = useState({
    tags: '',
    logged: 1,
  });

  useEffect(() => {
    if (
      userId !== '-1' &&
      isLoggedIn &&
      id &&
      source === 'rolesAtLeadingBrands'
    ) {
      setAdOptions({
        tags:
          tags && tags?.length
            ? tags?.map((tag: ProfileTags) => tag?.tagId).join(',')
            : '',
        logged: 2,
      });
    }
  }, [isLoggedIn, userId, id, tags, source]);

  useLoadAds(
    slot,
    setAds,
    source == 'rolesAtLeadingBrands' ? adOptions : options,
    false,
    !adsInfo[0]?.title,
    setAdsLoaded,
    source !== 'rolesAtLeadingBrands' || adOptions?.tags != ''
  );

  const callEvent = (type: 'view' | 'click', companyId: string | number) => {
    if (adsEventInfo && adsEventInfo[type])
      handleTracking(adsEventInfo[type], {
        event_category: adsEventInfo['eventCategory'],
        Origin: page,
        CompanyId: companyId,
        Source: 'Search',
        Type: type,
        UserId: sessionStorage.getItem('userId') || 'NA',
        Device: sessionStorage.getItem('device') || 'desktop',
        Device_Type: sessionStorage.getItem('deviceType') || 'web',
        Ref: 'logo',
        Status: sessionStorage.getItem('status') || 'LoggedOut',
      });
  };
  const {ref, isInView} = useInView({threshold: 0.5});
  const [eventFired, setEventFired] = useState(false);

  useEffect(() => {
    if (ads?.length) {
      callEvent('view', 0);
    }
    if (!eventFired && isInView && ads.length) {
      const normalizedLabel = label?.toLowerCase() || '';
      let source = '';

      if (normalizedLabel.includes('featured employers')) {
        source = 'Featured Employer';
      } else if (normalizedLabel.includes('diversity conscious employers')) {
        source = 'Diversity Conscious';
      } else if (normalizedLabel.includes('leading brands')) {
        source = 'Leading Brands';
      }

      if (source) {
        const quoteLineNoArray = ads.map(ad => ad.quoteLineNo);
        const custIdsArray = ads.map(ad => ad.custId);
        const centralCompanyIdsArray = ads.map(ad => ad.centralisedCompanyId);
        const hasQuoteLineNo = quoteLineNoArray.some(line => line != null);
        const hasCustId = custIdsArray.some(line => line != null);
        const hasCentralCompanyId = centralCompanyIdsArray.some(
          line => line != null
        );
        TrackEventUBA('companyLogosView', 'view', {
          categoryID: page === 'category' ? categoryId : undefined,
          tagID: page === 'tag' ? Number(tagId) : undefined,
          page: getUbaPageName(),
          sessionUsername: sessionStorage.getItem('userId') || '',
          source,
          companyIDSArray: ads.map(
            ad => ad.companyId || parseShowcaseUrl(ad.link)
          ),
          companyNamesArray: ads.map(ad => ad.title).filter(Boolean),
          ...(hasCustId && {custIdsArray}),
          ...(hasQuoteLineNo && {quoteLineNoArray}),
          ...(hasCentralCompanyId && {centralCompanyIdsArray}),
        });
        setEventFired(true);
      }
    }
  }, [isInView, ads, eventFired, label]);

  const loadedAds = adsInfo[0]?.title ? adsInfo : ads;

  const borderStyles = {
    borderRadius: '12px',
    border: '0.5px solid',
    borderColor: '#7E7E7E33',
  };

  const groupAds = (
    adsInfo: any,
    source?: string,
    isMdOrLg?: boolean,
    isXsOrXss?: boolean
  ) => {
    const group: any = [];
    let singleGroup: any = [];

    let adsPerRow = 4;
    if (source === 'rolesAtLeadingBrands' && (isMdOrLg || isXsOrXss)) {
      adsPerRow = 3;
    }

    adsInfo.forEach((v: object) => {
      singleGroup.push(v);
      if (singleGroup.length === adsPerRow) {
        group.push(singleGroup);
        singleGroup = [];
      }
    });

    if (adsInfo.length % adsPerRow > 0) {
      const pendingElements = adsPerRow - (adsInfo.length % adsPerRow);
      for (let i = 0; i < pendingElements; i++) {
        singleGroup.push({empty: true});
      }
    }
    group.push(singleGroup);
    return group;
  };

  const categoryId = useSelector(
    (state: AppState) => state.app.context.navigation.actualCategory
  );

  const tagId = useSelector(
    (state: AppState) => state.app.context.navigation.tag
  );

  const router = useRouter();
  const trackEvent = (
    adItem: {
      courseRefCode: number | string;
      id?: string;
      title?: string;
      companyId?: string;
      link: string;
      courseId?: string;
      custId?: string;
      quoteLineNo?: string;
      centralisedCompanyId?: string;
    },
    action: string
  ) => {
    let eventName = 'courseClick';
    let source: string | undefined;
    const eventData: Record<string, any> = {
      categoryID: page === 'category' ? categoryId : undefined,
      tagID: page === 'tag' ? Number(tagId) : undefined,
      page: getUbaPageName(),
      sessionUsername: sessionStorage.getItem('userId') || '',
      ref: new URL(window.location.href).searchParams.get('ref') || '',
    };

    if (label?.toLowerCase()?.includes('institute')) {
      eventName = 'featuredInstituteClick';
      eventData.featuredInstituteName = adItem.title || '';
      eventData.featuredInstituteId = adItem.id || '';
    }
    if (label && label.toLowerCase().includes('employer')) {
      eventName = 'companyLogosClick';
      source = 'Featured Employer';

      eventData.companyID =
        adItem?.companyId || parseShowcaseUrl(adItem?.link) || '';

      eventData.companyName = adItem?.title || '';
    }

    if (label && label.toLowerCase().includes('conscious')) {
      eventName = 'companyLogosClick';
      source = 'Diversity Conscious';

      eventData.companyID =
        adItem?.companyId || parseShowcaseUrl(adItem?.link) || '';

      eventData.companyName = adItem?.title || '';
    }

    if (label && label.toLowerCase().includes('leading brands')) {
      eventName = 'companyLogosClick';
      source = 'Leading Brands';
      eventData.companyID =
        adItem?.companyId || parseShowcaseUrl(adItem?.link) || '';
      eventData.companyName = adItem?.title || '';
    }

    if (eventName === 'companyLogosClick') {
      if (adItem?.custId != null) eventData.custId = adItem.custId;
      if (adItem?.quoteLineNo != null) eventData.quoteLineNo = adItem.quoteLineNo;
      if (adItem?.centralisedCompanyId != null) {
        eventData.centralCompanyID = adItem.centralisedCompanyId;
      }
    }

    if (eventName === 'courseClick') {
      eventData.courseID = adItem?.courseId || '';
      eventData.refInv =
        getQueryParamsAndSeekerId(adItem.link).queryParams['refinv'] || '';
      eventData.refOrg =
        getQueryParamsAndSeekerId(adItem.link).queryParams['reforg'] || '';
      eventData.courseRefCode = router.query.ref || '';
    }
    if (source) {
      eventData.source = source || '';
    }

    TrackEventUBA(eventName, action, eventData);
  };

  useEffect(() => {
    if (loadedAds?.length > 0) {
      setShowSkeleton(false);
    } else if (adsLoaded) {
      setShowSkeleton(false);
    }
  }, [loadedAds, adsLoaded]);

  return (
    <div ref={ref}>
      <AnimatePresence mode="wait">
        {!showSkeleton ? (
          <motion.div
            key="vertical-frame-content"
            initial={{opacity: 0, y: -10}}
            animate={{opacity: 1, y: 0}}
            exit={{opacity: 0, y: 10}}
            transition={{duration: 0.15}}
          >
            {titleOut && loadedAds?.length > 0 && (
              <Typography
                fontSize={16}
                fontWeight={500}
                color="secondary.main"
                sx={{mb: 1.5}}
              >
                {label}
              </Typography>
            )}
            <Card
              sx={{
                borderRadius: titleOut ? 2 : 4,
                px: 1.5,
                py: 2,
                mb: titleOut ? 3.75 : 2,
                display: loadedAds?.length > 0 ? 'block' : 'none',
              }}
              data-testid={`job_ads_banner_${label
                ?.toLowerCase()
                ?.replace(/ /g, '-')}`}
              id={`job_ads_banner_${label?.toLowerCase()?.replace(/ /g, '-')}`}
            >
              {!titleOut && (
                <Typography
                  fontSize={14}
                  fontWeight={500}
                  color="secondary.main"
                  sx={{mb: 2}}
                >
                  {label}
                </Typography>
              )}
              {direction === 'row' ? (
                <Grid container columnSpacing={3.5} rowSpacing={2}>
                  {loadedAds?.map((data: any, i: number) => {
                    const imgUrl = data?.companyLogo || data?.imageUrl;

                    return (
                      <Grid
                        item
                        md={showTitle ? 12 : undefined}
                        key={`ads-v2-title-${i}`}
                        sx={{minWidth: '100%'}}
                      >
                        <Box
                          sx={
                            showTitle
                              ? {
                                  ...borderStyles,
                                  p: 1,
                                }
                              : {}
                          }
                        >
                          <Link
                            title={data?.title}
                            href={data?.link ?? '#'}
                            key={`ads-v2-link-${i}`}
                            target="_blank"
                            rel="nofollow"
                            onClick={event => {
                              event?.preventDefault();
                              deferTask(() => {
                                if (data?.link && data?.link !== '#') {
                                  openUrl(data?.link, '_blank');
                                }
                                callEvent('click', data?.title);
                                trackEvent(data, 'click');
                              });
                            }}
                            sx={{
                              alignItems: 'center',
                              display: 'flex',
                              gap: 1,
                              color: 'gray.dark',
                              textDecoration: 'none',
                            }}
                          >
                            <Box
                              sx={{
                                ...borderStyles,
                                overflow: 'hidden',
                                width: 50,
                                height: 50,
                                position: 'relative',
                              }}
                            >
                              {imgUrl && (
                                <Image
                                  src={imgUrl}
                                  alt={data?.title}
                                  layout="fill"
                                  objectFit="contain"
                                  quality={100}
                                  blurDataURL={
                                    data?.companyLogo || data?.imageUrl
                                  }
                                />
                              )}
                            </Box>
                            {showTitle && (
                              <Typography
                                fontSize={14}
                                color={'secondary.main'}
                              >
                                {data?.title}
                              </Typography>
                            )}
                          </Link>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              ) : (
                <Box
                  display="flex"
                  flexDirection="column"
                  columnGap={3.5}
                  rowGap={2}
                >
                  {(source === 'rolesAtLeadingBrands'
                    ? groupAds(loadedAds, source, isMdOrLg, isXsOrXss)
                    : groupAds(loadedAds)
                  )?.map((group: any, i: number) => (
                    <Box
                      key={`ads-v2-group-box-${i}`}
                      display={
                        source === 'rolesAtLeadingBrands' &&
                        (isXsOrXss || isMdOrLg)
                          ? 'grid'
                          : 'flex'
                      }
                      {...(source === 'rolesAtLeadingBrands' &&
                      (isXsOrXss || isMdOrLg)
                        ? {
                            gridTemplateColumns: '1fr 1fr 1fr',
                            alignItems: 'center',
                            justifyItems: 'center',
                          }
                        : {
                            justifyContent: 'space-between',
                          })}
                    >
                      {group.map((data: any, j: number) => {
                        const companyId = data?.link
                          ? parseShowcaseUrl(data?.link)
                          : 0;

                        if (data.empty) {
                          return (
                            <Box
                              key={`ads-v2-group-${i}-${j}`}
                              {...(source === 'rolesAtLeadingBrands' &&
                              (isXsOrXss || isMdOrLg)
                                ? {} // Empty grid slot
                                : {width: 50, height: 50})}
                            />
                          );
                        }

                        const imgUrl = data?.companyLogo || data?.imageUrl;

                        return (
                          <Tooltip
                            title={data.title}
                            arrow
                            key={`ads-v2-companylogo-group-${i}-${j}`}
                          >
                            <Box
                              sx={{
                                ...(showTitle ? {...borderStyles, p: 1} : {}),
                                ...(source === 'rolesAtLeadingBrands'
                                  ? {
                                      flex: 1,
                                      display: 'flex',
                                      justifyContent: 'center',
                                    }
                                  : {}),
                              }}
                            >
                              <Link
                                title={data?.title}
                                href={data?.link ?? '#'}
                                target="_blank"
                                rel="nofollow"
                                sx={{
                                  alignItems: 'center',
                                  display: 'flex',
                                  gap: 1,
                                  color: 'gray.dark',
                                  textDecoration: 'none',
                                }}
                                onClick={event => {
                                  event?.preventDefault();
                                  deferTask(() => {
                                    if (data?.link && data?.link !== '#') {
                                      openUrl(data?.link, '_blank');
                                    }
                                    callEvent('click', companyId);
                                    trackEvent(data, 'click');
                                  });
                                }}
                              >
                                <Box
                                  sx={{
                                    ...borderStyles,
                                    overflow: 'hidden',
                                    width:
                                      source === 'rolesAtLeadingBrands'
                                        ? 75
                                        : 50,
                                    height: 50,
                                    position: 'relative',
                                  }}
                                >
                                  {imgUrl && (
                                    <Image
                                      src={imgUrl}
                                      alt={data?.title}
                                      layout="fill"
                                      objectFit="contain"
                                      quality={100}
                                      blurDataURL={imgUrl}
                                      style={{
                                        ...(source ===
                                          'rolesAtLeadingBrands' && {
                                          padding: '4px',
                                        }),
                                      }}
                                    />
                                  )}
                                </Box>
                                {showTitle && (
                                  <Typography
                                    fontSize={14}
                                    color="secondary.main"
                                  >
                                    {data?.title}
                                  </Typography>
                                )}
                              </Link>
                            </Box>
                          </Tooltip>
                        );
                      })}
                    </Box>
                  ))}
                </Box>
              )}
            </Card>
          </motion.div>
        ) : source === 'featuredEmployer' ||
          source === 'diversity' ||
          source === 'wowFeaturedEmployer' ||
          (showTitle && direction === 'row') ? (
          <motion.div
            key="vertical-frame-skeleton"
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            transition={{duration: 0.2}}
          >
            <Card
              sx={{
                borderRadius: titleOut ? 2 : 4,
                px: 1.5,
                py: 2,
                mb: titleOut ? 3.75 : 2,
              }}
              data-testid={`job_ads_banner_${label
                ?.toLowerCase()
                ?.replace(/ /g, '-')}-skeleton`}
            >
              <Skeleton
                variant="rounded"
                width="100%"
                height={
                  source === 'featuredEmployer' ||
                  source === 'wowFeaturedEmployer'
                    ? 433
                    : source === 'diversity'
                    ? 219
                    : 109
                }
                sx={{
                  borderRadius: titleOut ? 2 : 4,
                }}
              />
            </Card>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
};

export default VerticalFrame;
